'use client'

import React, { useRef, useEffect } from 'react'
import { useReducedMotion } from 'framer-motion'

export interface KineticGridProps {
  background?: string
  dotColor?: string
  lineColor?: string
  trailColor?: string
  spacing?: number
  radius?: number
  strength?: number
  trail?: boolean
  style?: React.CSSProperties
  className?: string
}

interface Dot {
  hx: number
  hy: number
  x: number
  y: number
  vx: number
  vy: number
}

interface TrailPoint {
  x: number
  y: number
  t: number
}

export default function KineticGrid({
  background = 'transparent',
  dotColor = '#a3e635',
  lineColor = 'rgba(163, 230, 53, 0.45)',
  trailColor = '#a3e635',
  spacing = 45,
  radius = 280,
  strength = 5,
  trail = true,
  style = {},
  className = '',
}: KineticGridProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const mouseRef = useRef({ x: -9999, y: -9999, active: false })
  const trailRef = useRef<TrailPoint[]>([])
  const prefersReducedMotion = useReducedMotion()

  useEffect(() => {
    const host = hostRef.current
    const canvas = canvasRef.current
    if (!host || !canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const GAP = Math.max(8, spacing)
    const R = Math.max(1, radius)
    // The cursor-magnetic pull and trail are reactive parallax-style motion — exactly what
    // reduced motion targets — so both are dialed way down instead of removed, leaving the
    // grid itself fully visible and only barely reactive rather than blank.
    const PULL = (Math.max(1, Math.min(10, strength)) / 10) * 4 * (prefersReducedMotion ? 0.12 : 1)
    const showTrail = trail && !prefersReducedMotion
    let W = 1
    let H = 1
    let cols: Dot[][] = []
    let dots: Dot[] = []

    const build = (mw?: number, mh?: number) => {
      const r = host.getBoundingClientRect()
      W = Math.max(1, Math.floor(mw ?? r.width))
      H = Math.max(1, Math.floor(mh ?? r.height))
      const dpr = window.devicePixelRatio || 1
      canvas.width = Math.floor(W * dpr)
      canvas.height = Math.floor(H * dpr)
      canvas.style.width = W + 'px'
      canvas.style.height = H + 'px'
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      cols = []
      dots = []
      const PAD = GAP * 4
      const nCols = Math.floor((W + PAD * 2) / GAP) + 2
      const nRows = Math.floor((H + PAD * 2) / GAP) + 2
      for (let c = 0; c < nCols; c++) {
        const col: Dot[] = []
        for (let rIdx = 0; rIdx < nRows; rIdx++) {
          const hx = -PAD + c * GAP
          const hy = -PAD + rIdx * GAP
          const d: Dot = { hx, hy, x: hx, y: hy, vx: 0, vy: 0 }
          col.push(d)
          dots.push(d)
        }
        cols.push(col)
      }
    }

    build()
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver((entries) => {
            const cr = entries[0]?.contentRect
            build(cr?.width, cr?.height)
          })
        : null
    ro?.observe(host)

    const setMouse = (clientX: number, clientY: number) => {
      if (!canvas) return
      const r = canvas.getBoundingClientRect()
      const mx = clientX - r.left
      const my = clientY - r.top
      mouseRef.current.x = mx
      mouseRef.current.y = my
      mouseRef.current.active = true
      if (showTrail) {
        const now = performance.now()
        const trailList = trailRef.current
        trailList.push({ x: mx, y: my, t: now })
        if (trailList.length > 80) trailList.shift()
      }
    }

    const onMove = (e: MouseEvent) => setMouse(e.clientX, e.clientY)
    const onLeave = () => {
      mouseRef.current.active = false
      mouseRef.current.x = -9999
      mouseRef.current.y = -9999
    }
    const onTouch = (e: TouchEvent) => {
      const t = e.touches[0]
      if (t) setMouse(t.clientX, t.clientY)
    }

    window.addEventListener('mousemove', onMove)
    document.addEventListener('mouseleave', onLeave)
    window.addEventListener('touchmove', onTouch, { passive: true })
    window.addEventListener('touchend', onLeave)

    let raf = 0
    // Paused via IntersectionObserver below while scrolled off-screen — this loop redraws
    // the entire dot grid every frame regardless of motion, so there's no reason to keep
    // paying for it while nothing is visible.
    let isVisible = true
    const frame = () => {
      const m = mouseRef.current
      ctx.clearRect(0, 0, W, H)

      // Update dot physics: spring home + attraction toward cursor
      for (const d of dots) {
        // Perimeter damping: outer boundary dots resist inward pull so the grid never leaves the screen edge
        const isBoundary = d.hx <= 16 || d.hy <= 16 || d.hx >= W - 16 || d.hy >= H - 16
        const spring = isBoundary ? 0.35 : 0.08
        let ax = (d.hx - d.x) * spring
        let ay = (d.hy - d.y) * spring
        if (m.active && !isBoundary) {
          const dx = m.x - d.x
          const dy = m.y - d.y
          const dist = Math.sqrt(dx * dx + dy * dy)
          if (dist < R && dist > 0.001) {
            const f = (1 - dist / R) * PULL
            ax += (dx / dist) * f
            ay += (dy / dist) * f
          }
        }
        d.vx = (d.vx + ax) * 0.82
        d.vy = (d.vy + ay) * 0.82
        d.x += d.vx
        d.y += d.vy
      }

      // Grid mesh lines (brighten near the cursor)
      for (let c = 0; c < cols.length; c++) {
        for (let rIdx = 0; rIdx < cols[c].length; rIdx++) {
          const d = cols[c][rIdx]
          const right = cols[c + 1]?.[rIdx]
          const down = cols[c]?.[rIdx + 1]
          const prox = m.active
            ? Math.max(0, 1 - Math.sqrt((m.x - d.x) ** 2 + (m.y - d.y) ** 2) / R)
            : 0
          if (right) {
            ctx.globalAlpha = 0.06 + prox * 0.7
            ctx.strokeStyle = lineColor
            ctx.lineWidth = 0.5 + prox * 1.5
            ctx.beginPath()
            ctx.moveTo(d.x, d.y)
            ctx.lineTo(right.x, right.y)
            ctx.stroke()
          }
          if (down) {
            ctx.globalAlpha = 0.06 + prox * 0.7
            ctx.strokeStyle = lineColor
            ctx.lineWidth = 0.5 + prox * 1.5
            ctx.beginPath()
            ctx.moveTo(d.x, d.y)
            ctx.lineTo(down.x, down.y)
            ctx.stroke()
          }
        }
      }

      // Dots
      for (const d of dots) {
        const prox = m.active
          ? Math.max(0, 1 - Math.sqrt((m.x - d.x) ** 2 + (m.y - d.y) ** 2) / R)
          : 0
        ctx.globalAlpha = 0.22 + prox * 0.78
        ctx.fillStyle = dotColor
        ctx.beginPath()
        ctx.arc(d.x, d.y, 0.8 + prox * 2.2, 0, 2 * Math.PI)
        ctx.fill()
      }

      // Cursor trail line
      if (showTrail) {
        const now = performance.now()
        const tr = trailRef.current
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        for (let i = 1; i < tr.length; i++) {
          const a = tr[i - 1]
          const b = tr[i]
          const age = now - b.t
          if (age > 260) continue
          ctx.globalAlpha = Math.max(0, 1 - age / 260) * 0.85
          ctx.strokeStyle = trailColor
          ctx.lineWidth = 2
          ctx.beginPath()
          ctx.moveTo(a.x, a.y)
          ctx.lineTo(b.x, b.y)
          ctx.stroke()
        }
      }
      ctx.globalAlpha = 1
      raf = isVisible ? requestAnimationFrame(frame) : 0
    }

    raf = requestAnimationFrame(frame)

    const intersectionObserver = new IntersectionObserver(([entry]) => {
      isVisible = entry.isIntersecting
      if (isVisible && raf === 0) {
        raf = requestAnimationFrame(frame)
      }
    }, { threshold: 0.05 })
    intersectionObserver.observe(host)

    return () => {
      intersectionObserver.disconnect()
      cancelAnimationFrame(raf)
      ro?.disconnect()
      window.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseleave', onLeave)
      window.removeEventListener('touchmove', onTouch)
      window.removeEventListener('touchend', onLeave)
    }
  }, [background, dotColor, lineColor, trailColor, spacing, radius, strength, trail, prefersReducedMotion])

  return (
    <div
      ref={hostRef}
      className={`relative w-full h-full overflow-hidden select-none ${className}`}
      style={{
        background,
        cursor: 'crosshair',
        ...style,
      }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
      />
    </div>
  )
}
